import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, ShoppingBag, Clock, Truck, MapPin, Package } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export default function B2BPage() {
  const { user, profile, logout, loading: authLoading, isB2B, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (isAdmin) {
        navigate('/admin');
      }
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (user && isB2B) {
      fetchOrders();
    }
  }, [user, isB2B]);

  const fetchOrders = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      .eq('b2b_user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) setOrders(data);
    setLoading(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const statusColors = {
    pending: 'bg-yellow-50 text-yellow-600',
    confirmed: 'bg-blue-50 text-blue-600',
    preparing: 'bg-purple-50 text-purple-600',
    ready: 'bg-green-50 text-green-600',
    delivered: 'bg-emerald-50 text-emerald-700',
    cancelled: 'bg-red-50 text-red-600',
  };

  const statusLabels = {
    pending: 'Pendiente',
    confirmed: 'Confirmado',
    preparing: 'Preparando',
    ready: 'Listo para retiro',
    delivered: 'Entregado',
    cancelled: 'Cancelado',
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2C5530] border-t-transparent"></div>
      </div>
    );
  }

  if (!user || !isB2B) return null;

  const totalSpent = orders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + o.total, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen pt-20 md:pt-24 pb-16 bg-[#FAFAF7]"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-cabinet font-bold text-2xl sm:text-3xl text-[#1A231C]">Mi Cuenta Empresa</h1>
            <p className="text-[#808E83]">
              {profile?.name} {profile?.company ? `· ${profile.company}` : ''}
            </p>
          </div>
          <button onClick={handleLogout} className="inline-flex items-center gap-2 text-[#4A5D4E] hover:text-red-500 transition-colors">
            <LogOut size={18} /> Cerrar sesión
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <ShoppingBag className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#808E83]">Total Pedidos</p>
                <p className="font-cabinet font-bold text-xl">{orders.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <Package className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#808E83]">Total Compras</p>
                <p className="font-cabinet font-bold text-xl">${totalSpent.toLocaleString('es-CL')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                <Clock className="text-yellow-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#808E83]">Pendientes</p>
                <p className="font-cabinet font-bold text-xl">{orders.filter(o => o.status === 'pending' || o.status === 'confirmed' || o.status === 'preparing').length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Orders */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="font-cabinet font-semibold text-xl mb-6">Historial de Pedidos</h2>

          {loading ? (
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2C5530] border-t-transparent mx-auto"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="py-12 text-center text-[#808E83]">
              <ShoppingBag className="mx-auto mb-4" size={40} />
              <p className="mb-2">Aún no tienes pedidos</p>
              <p className="text-sm">Agrega productos al carrito para hacer tu primer pedido</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order.id} className="border border-[#E8EAE6] rounded-xl overflow-hidden">
                  <div
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 cursor-pointer hover:bg-[#FAFAF7]"
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-cabinet font-bold text-sm text-[#808E83]">#{order.id}</span>
                      <div>
                        <span className="text-xs text-[#808E83]">{new Date(order.created_at).toLocaleString('es-CL')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2 sm:mt-0">
                      <span className="text-xs flex items-center gap-1">
                        {order.delivery_type === 'pickup' ? <MapPin size={12} /> : <Truck size={12} />}
                        {order.delivery_type === 'pickup' ? 'Retiro' : order.delivery_zone}
                      </span>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColors[order.status]}`}>
                        {statusLabels[order.status]}
                      </span>
                      <strong className="text-sm text-[#2C5530]">${order.total.toLocaleString('es-CL')}</strong>
                    </div>
                  </div>

                  {expandedOrder === order.id && (
                    <div className="border-t border-[#E8EAE6] p-4 bg-[#FAFAF7]">
                      <div className="space-y-1">
                        {order.order_items?.map(item => (
                          <div key={item.id} className="flex justify-between text-sm text-[#4A5D4E]">
                            <span>{item.product_name} x{item.quantity}</span>
                            <span>${item.subtotal.toLocaleString('es-CL')}</span>
                          </div>
                        ))}
                        {order.delivery_cost > 0 && (
                          <div className="flex justify-between text-sm text-[#4A5D4E] pt-1 border-t">
                            <span>Envío</span>
                            <span>${order.delivery_cost.toLocaleString('es-CL')}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-semibold pt-1 border-t">
                          <span>Total</span>
                          <span>${order.total.toLocaleString('es-CL')}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
